"use client";

import cnBind from "classnames/bind";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type PointerEvent as ReactPointerEvent,
} from "react";

import { ModalCallBack } from "@/components/_Modals/ModalCallBack";
import type { IndustrySlide } from "@/data/industryPresets";
import { useOnboardModal } from "@/shared/hooks/useOnboardModal";
import { toLeadIndustryPreset } from "@/shared/utils/toLeadIndustryPreset";

import { useIndustryPreset } from "./IndustryPresetContext";

import styles from "./IndustrySiteShowcase.module.scss";

const cx = cnBind.bind(styles);

type BrowserDeckProps = {
    title: string;
    lead: string;
    images: string[];
    captions?: string[];
    ariaLabel: string;
    reduceMotion: boolean | null;
    deckKey: string;
};

const BrowserDeck = ({
    title,
    lead,
    images,
    captions,
    ariaLabel,
    reduceMotion,
    deckKey,
}: BrowserDeckProps) => {
    const [slide, setSlide] = useState(0);
    const dragStartX = useRef<number | null>(null);

    useEffect(() => {
        setSlide(0);
    }, [deckKey]);

    useEffect(() => {
        if (reduceMotion || images.length <= 1) return undefined;
        const id = window.setInterval(() => {
            setSlide((current) => (current + 1) % images.length);
        }, 4800);
        return () => window.clearInterval(id);
    }, [deckKey, images.length, reduceMotion]);

    const go = useCallback(
        (dir: -1 | 1) => {
            if (images.length <= 1) return;
            setSlide((current) => (current + dir + images.length) % images.length);
        },
        [images.length],
    );

    const onPointerDown = (event: ReactPointerEvent) => {
        dragStartX.current = event.clientX;
    };

    const onPointerUp = (event: ReactPointerEvent) => {
        if (dragStartX.current == null) return;
        const delta = event.clientX - dragStartX.current;
        dragStartX.current = null;
        if (Math.abs(delta) < 36) return;
        go(delta < 0 ? 1 : -1);
    };

    const activeSrc = images[slide] ?? images[0];
    const activeCaption = captions?.[slide];
    const slideTransition = reduceMotion
        ? { duration: 0 }
        : { duration: 0.55, ease: "easeInOut" as const };

    return (
        <div className={cx("deck")}>
            <div className={cx("head")}>
                <h2>{title}</h2>
                <p>{lead}</p>
            </div>

            <div className={cx("browser")}>
                <div
                    className={cx("viewport")}
                    onPointerDown={onPointerDown}
                    onPointerUp={onPointerUp}
                    onPointerCancel={() => {
                        dragStartX.current = null;
                    }}
                    role="region"
                    aria-roledescription="carousel"
                    aria-label={ariaLabel}
                >
                    <div className={cx("slideFrame")}>
                        <AnimatePresence initial={false}>
                            {activeSrc ? (
                                <motion.img
                                    key={activeSrc}
                                    className={cx("slideImage")}
                                    src={activeSrc}
                                    alt={
                                        activeCaption
                                            ? `${title}: ${activeCaption}`
                                            : `${title} — скриншот веб-витрины TEZAM, слайд ${slide + 1}`
                                    }
                                    loading="lazy"
                                    draggable={false}
                                    initial={reduceMotion ? false : { opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={reduceMotion ? undefined : { opacity: 0 }}
                                    transition={slideTransition}
                                />
                            ) : null}
                        </AnimatePresence>
                    </div>

                    {images.length > 1 ? (
                        <>
                            <button
                                type="button"
                                className={cx("nav", "navPrev")}
                                onClick={() => go(-1)}
                                aria-label="Предыдущий скрин"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                className={cx("nav", "navNext")}
                                onClick={() => go(1)}
                                aria-label="Следующий скрин"
                            >
                                ›
                            </button>
                            <div className={cx("dots")} role="tablist" aria-label="Слайды">
                                {images.map((src, index) => (
                                    <button
                                        key={src}
                                        type="button"
                                        role="tab"
                                        aria-selected={index === slide}
                                        className={cx("dot", index === slide && "dotActive")}
                                        onClick={() => setSlide(index)}
                                        aria-label={`Слайд ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>

            {activeCaption ? (
                <AnimatePresence mode="wait" initial={false}>
                    <motion.p
                        key={activeCaption}
                        className={cx("caption")}
                        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                        transition={slideTransition}
                    >
                        {activeCaption}
                    </motion.p>
                </AnimatePresence>
            ) : null}
        </div>
    );
};

function slidesToImages(slides: IndustrySlide[]): string[] {
    return slides.map((slide) => slide.image);
}

function slidesToCaptions(slides: IndustrySlide[]): string[] {
    return slides.map((slide) => slide.caption);
}

/** Browser mockup + mini image slider + short headline + trial CTA. */
export const IndustrySiteShowcase = () => {
    const reduceMotion = useReducedMotion();
    const { activePreset, preset } = useIndustryPreset();
    const { isModalOpen, leadIntent, openSaasModal, closeModal } = useOnboardModal();
    const siteImages = preset.siteImages.length > 0 ? preset.siteImages : [];
    const crmSlides = preset.crmSlides ?? [];

    const openTrial = () => {
        openSaasModal("demo", "Demo", {
            industryPreset: toLeadIndustryPreset(activePreset),
            leadSource: `homepage_site_showcase_${activePreset}`,
        });
    };

    return (
        <section
            className={cx("section")}
            style={
                {
                    ["--preset-accent" as string]: preset.accent,
                    ["--preset-accent-soft" as string]: preset.accentSoft,
                } as CSSProperties
            }
        >
            <div className={cx("wrapper", "container")}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activePreset}
                        className={cx("inner")}
                        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? undefined : { opacity: 0 }}
                        transition={{ duration: 0.22 }}
                    >
                        {siteImages.length > 0 ? (
                            <BrowserDeck
                                deckKey={`${activePreset}-site`}
                                title={preset.siteTitle}
                                lead={preset.siteLead}
                                images={siteImages}
                                ariaLabel={`Скриншоты сайта · ${preset.shortLabel}`}
                                reduceMotion={reduceMotion}
                            />
                        ) : null}

                        {preset.crmTitle && crmSlides.length > 0 ? (
                            <BrowserDeck
                                deckKey={`${activePreset}-crm`}
                                title={preset.crmTitle}
                                lead={preset.crmLead ?? ""}
                                images={slidesToImages(crmSlides)}
                                captions={slidesToCaptions(crmSlides)}
                                ariaLabel={`CRM · ${preset.shortLabel}`}
                                reduceMotion={reduceMotion}
                            />
                        ) : null}

                        <button type="button" className={cx("cta")} onClick={openTrial}>
                            {preset.siteCta}
                        </button>
                    </motion.div>
                </AnimatePresence>
            </div>

            <ModalCallBack isOpen={isModalOpen} onClose={closeModal} leadIntent={leadIntent} />
        </section>
    );
};
